// Phase JB.2 — useTrigger hook.
//
// Returns the latest UN-DISMISSED, FIRED trigger_evaluations row for
// the auth user, plus a dismiss function. Anon → returns no trigger.

import { useCallback, useEffect, useId, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import { useAuthSession } from "../auth/useAuthSession.ts";

export interface TriggerEvaluation {
  eval_id: string;
  user_id: string;
  rule_id: string;
  evaluated_at: string;
  fired: boolean;
  payload: Record<string, unknown>;
  dismissed_at: string | null;
}

export interface UseTriggerResult {
  trigger: TriggerEvaluation | null;
  loading: boolean;
  error: string | null;
  dismiss: () => Promise<void>;
}

export function useTrigger(): UseTriggerResult {
  const { user } = useAuthSession();
  const instanceId = useId();
  const [trigger, setTrigger] = useState<TriggerEvaluation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setTrigger(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const sb = getBrowserClient();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (sb.from("trigger_evaluations" as never) as any)
      .select("eval_id,user_id,rule_id,evaluated_at,fired,payload,dismissed_at")
      .eq("user_id", user.id)
      .eq("fired", true)
      .is("dismissed_at", null)
      .order("evaluated_at", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then((res: { data: TriggerEvaluation | null; error: { message: string } | null }) => {
        if (cancelled) return;
        if (res.error) setError(res.error.message);
        else setTrigger(res.data ?? null);
        setLoading(false);
      });

    const channel = sb
      .channel(`trigger_evaluations:${user.id}:${instanceId}`)
      .on(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        "postgres_changes" as any,
        {
          event: "INSERT",
          schema: "public",
          table: "trigger_evaluations",
          filter: `user_id=eq.${user.id}`,
        },
        (payload: { new: TriggerEvaluation }) => {
          if (payload.new.fired && payload.new.dismissed_at === null) {
            setTrigger(payload.new);
          }
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      void sb.removeChannel(channel);
    };
  }, [user, instanceId]);

  const dismiss = useCallback(async (): Promise<void> => {
    if (!trigger || !user) return;
    const sb = getBrowserClient();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: e } = await (sb.from("trigger_evaluations" as never) as any)
      .update({ dismissed_at: new Date().toISOString() })
      .eq("eval_id", trigger.eval_id);
    if (e) setError(e.message);
    else setTrigger(null);
  }, [trigger, user]);

  return { trigger, loading, error, dismiss };
}
